import React from "react";
import theme from "../../data/theme";
import { roomTemp } from "../../utils/roomTemp";

/**
 * RoomRead — what the room has spent, against what the board expected.
 *
 * 🔴 COPY RULE, NON-NEGOTIABLE: DESCRIBE, NEVER INSTRUCT.
 * "The room has spent $104 less than these players usually go for." Never "buy
 * now", never a suggested bid. Our board has measured calibration gaps, and
 * prescriptive advice fails at exactly the moment the user is most exposed.
 * Every string in this file is a statement of fact about what has happened.
 *
 * 🔴 SAY WHAT WE MEASURE AGAINST. The comparable product's version of this line
 * is "Room vs Blended AAV". Ours is tier mids fitted on 99 real 2025 auctions
 * (docs/reference/room-bands-aug10.md). One line of copy carries the entire
 * product difference, so it is on screen, not implicit.
 *
 * ±30 IS NOT SHOWN. It is an engine threshold (auction.js:888-896) — 15% of a
 * team's whole pool at $200 — and is reached routinely. The human-facing
 * numbers are the signed tally and the per-slot figure, which is banded
 * per-decile of the draft and rescaled to $200-equivalent before comparison.
 * Displayed dollars stay the league's own.
 */
const BAND_COPY = {
  "very-cold": { label: "RUNNING VERY COLD", color: "#60a5fa" },
  cold: { label: "RUNNING COLD", color: "#7dd3fc" },
  normal: { label: "RUNNING NORMAL", color: theme.colors.text.secondary },
  hot: { label: "RUNNING HOT", color: "#fbbf24" },
  "very-hot": { label: "RUNNING VERY HOT", color: "#f87171" },
};

const money = (n) => `${n < 0 ? "−" : "+"}$${Math.abs(Math.round(n))}`;

export default function RoomRead({ board, openSlots, dynamicPrices, compact = false }) {
  const temp = roomTemp(board.sales, { budget: board.budget }, dynamicPrices, openSlots);
  const band = temp.band ? BAND_COPY[temp.band] : null;
  const sentence = temp.tally === 0
    ? "The room is level with what these players usually go for."
    : `The room has spent $${Math.abs(Math.round(temp.tally))} ${temp.tally < 0 ? "less" : "more"} than these players usually go for.`;

  const chip = (pos, v) => (
    <span key={pos} style={{
      display: "inline-flex", alignItems: "baseline", gap: 5, padding: "3px 9px", borderRadius: 999,
      background: theme.colors.posBg[pos], border: `1px solid ${theme.colors.posBorder[pos]}`,
      fontSize: theme.fontSize.caption, fontVariantNumeric: "tabular-nums",
    }}>
      <strong style={{ color: theme.colors.pos[pos], fontWeight: theme.fontWeight.heavy }}>{pos}</strong>
      <span style={{ color: v < 0 ? "#7dd3fc" : v > 0 ? "#fbbf24" : theme.colors.text.faint }}>{money(v)}</span>
    </span>
  );

  // ── COMPACT: one line, for the pinned entry bar. ──
  // The yardstick attribution stays ON SCREEN rather than becoming a tooltip.
  // "Measured against tier mids from 99 real auctions" is the one line of copy
  // that carries the whole product difference against a blended-AAV competitor;
  // demoting it to a hover would be quietly dropping it.
  if (compact) {
    return (
      <div style={{
        display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
        fontSize: theme.fontSize.caption, minWidth: 0,
      }}>
        <span style={{
          fontFamily: theme.fonts.display, letterSpacing: "0.05em", whiteSpace: "nowrap",
          fontWeight: theme.fontWeight.heavy, color: band ? band.color : theme.colors.text.faint,
        }}>{band ? `ROOM ${band.label}` : "ROOM"}</span>
        <span style={{
          fontSize: theme.fontSize.sub, fontWeight: theme.fontWeight.heavy,
          color: temp.tally < 0 ? "#7dd3fc" : temp.tally > 0 ? "#fbbf24" : theme.colors.text.secondary,
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>{money(temp.tally)}</span>
        {temp.perSlot != null && (
          <span style={{ color: theme.colors.text.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {money(temp.perSlot)} / slot
          </span>
        )}
        <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {["QB", "RB", "WR", "TE"].map(pos => chip(pos, temp.byPos[pos]))}
        </span>
        <span style={{ color: theme.colors.text.faint, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {temp.nSold} sold · {openSlots} left
        </span>
        <span style={{ color: theme.colors.text.faint, fontSize: theme.fontSize.micro, minWidth: 0 }}>
          vs tier mids from 99 real auctions
        </span>
      </div>
    );
  }

  return (
    <div style={{
      background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`,
      borderRadius: theme.radius.lg, padding: "12px 16px",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 18, flexWrap: "wrap" }}>
        <span style={{
          fontFamily: theme.fonts.display, fontSize: theme.fontSize.sub, letterSpacing: "0.06em",
          fontWeight: theme.fontWeight.heavy, color: band ? band.color : theme.colors.text.faint,
        }}>
          {band ? `ROOM ${band.label}` : "ROOM"}
        </span>
        <span style={{
          fontSize: theme.fontSize.title, fontWeight: theme.fontWeight.heavy,
          color: temp.tally < 0 ? "#7dd3fc" : temp.tally > 0 ? "#fbbf24" : theme.colors.text.secondary,
          fontVariantNumeric: "tabular-nums",
        }}>{money(temp.tally)}</span>
        {temp.perSlot != null && (
          <span style={{ fontSize: theme.fontSize.sub, color: theme.colors.text.muted, fontVariantNumeric: "tabular-nums" }}>
            {money(temp.perSlot)} <span style={{ fontSize: theme.fontSize.caption }}>/ open slot</span>
          </span>
        )}
        <span style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, fontVariantNumeric: "tabular-nums", marginLeft: "auto" }}>
          {temp.nSold} sold · {openSlots} slots left
          {temp.unpriced > 0 && ` · ${temp.unpriced} unpriced`}
        </span>
      </div>

      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
        {["QB", "RB", "WR", "TE"].map(pos => chip(pos, temp.byPos[pos]))}
      </div>

      <div style={{ marginTop: 10, fontSize: theme.fontSize.caption, color: theme.colors.text.muted, lineHeight: 1.5 }}>
        {sentence}{" "}
        <span style={{ color: theme.colors.text.faint }}>
          Measured against tier mid-prices fitted on 99 real 2025 auctions, not a blended AAV.
          {temp.unpriced > 0 && ` ${temp.unpriced} sale${temp.unpriced === 1 ? "" : "s"} off the board aren't counted.`}
        </span>
      </div>
    </div>
  );
}
